import { type JSX } from 'react';

import { Text } from 'Components/Text/Text';
import { iso8601ToUsTimestampLong } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { UserNameFormat, getUserNameFromSubject } from 'Helpers/UserUtils';
import { DiscussionMessage, DiscussionMessageType } from 'Models/DueDiligence';
import { PublicDiscussionMessage } from 'Models/TPRM';
import { UserResponse } from 'Models/User';

import styles from './MessageBubble.module.css';

export interface MessageBubbleAssessmentProps {
    perspective: 'assessment';
    clientName: string;
    message: DiscussionMessage;
    organizationName: string;
    users: UserResponse[];
}

export interface MessageBubbleQuestionnaireProps {
    perspective: 'questionnaire';
    clientName: string;
    message: PublicDiscussionMessage;
    organizationName: string;
}

export type MessageBubbleProps = MessageBubbleAssessmentProps | MessageBubbleQuestionnaireProps;

export const MessageBubble = (props: MessageBubbleProps): JSX.Element => {
    switch (props.perspective) {
        case 'assessment':
            return <MessageBubbleAssessment {...props} />;
        case 'questionnaire':
            return <MessageBubbleQuestionnaire {...props} />;
    }
};

const MessageBubbleAssessment = (props: MessageBubbleAssessmentProps): JSX.Element => {
    const [bubbleWrapperClass, bubbleClass, formattedMetadata, textColor] = (() => {
        const formattedTimestamp = iso8601ToUsTimestampLong(props.message.timestamp);

        switch (props.message._type) {
            case DiscussionMessageType.CLIENT:
                const fullName = getUserNameFromSubject(props.message.user_id, props.users, UserNameFormat.FIRST_SPACE_LAST);
                const formattedMetadataClient = `${fullName} | ${props.clientName} | ${formattedTimestamp}`;
                return [styles.messageWrapperRight, styles.messageRight, formattedMetadataClient, 'white' as const];
            case DiscussionMessageType.THIRD_PARTY:
                const formattedMetadataRespondent = `${props.message.user_email_address} | ${props.organizationName} | ${formattedTimestamp}`;
                return [styles.messageWrapperLeft, styles.messageLeft, formattedMetadataRespondent, 'blue' as const];
        }
    })();

    return (
        <div className={bubbleWrapperClass}>
            <div className={`${styles.messageBubble} ${bubbleClass}`}>
                <Text preserveLineBreaks color={textColor} variant="Text3" noStyles>
                    {props.message.text}
                </Text>
            </div>
            <Text variant="Text4" color="darkGray" noStyles>
                {formattedMetadata}
            </Text>
        </div>
    );
};

const MessageBubbleQuestionnaire = (props: MessageBubbleQuestionnaireProps): JSX.Element => {
    const [bubbleWrapperClass, bubbleClass, formattedMetadata, textColor] = (() => {
        const formattedTimestamp = iso8601ToUsTimestampLong(props.message.timestamp);

        switch (props.message._type) {
            case DiscussionMessageType.CLIENT:
                const fullName = `${props.message.user_first_name} ${props.message.user_last_name}`;
                const formattedMetadataClient = `${fullName} | ${props.clientName} | ${formattedTimestamp}`;
                return [styles.messageWrapperLeft, styles.messageLeft, formattedMetadataClient, 'blue' as const];
            case DiscussionMessageType.THIRD_PARTY:
                const formattedMetadataRespondent = `${props.message.user_email_address} | ${props.organizationName} | ${formattedTimestamp}`;
                return [styles.messageWrapperRight, styles.messageRight, formattedMetadataRespondent, 'white' as const];
        }
    })();

    return (
        <div className={bubbleWrapperClass}>
            <div className={`${styles.messageBubble} ${bubbleClass}`}>
                <Text preserveLineBreaks color={textColor} variant="Text3" noStyles>
                    {props.message.text}
                </Text>
            </div>
            <Text variant="Text4" color="darkGray" noStyles>
                {formattedMetadata}
            </Text>
        </div>
    );
};
